import React, {useState} from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import PrintoutDocumentRenderer from "../../PrintoutDocumentRenderer";
import PrintPreviewPageShell from "../../shell/PrintPreviewPageShell";
import PrintPreviewPrintButton from "../../shell/PrintPreviewPrintButton";
import PrintPreviewSidebarContent from "../../shell/PrintPreviewSidebarContent";
import {
    getPrintoutLayoutOptions,
    getPrintoutLayoutSelectionValue,
    parsePrintoutLayoutSelection,
    type PrintoutLayoutSelection,
} from "../../shell/printoutLayoutSelection";
import {usePrintoutDocumentVariables} from "../../shell/printoutDocumentVariables";
import {
    EMPTY_PRINT_VALUE,
    formatPrintNativeLanguage,
    formatPrintStatus,
} from "../../shell/printPreviewFormatters";
import {resolvePrintoutVariable} from "../../../variables/printoutVariableResolver";
import {useAgendaPrintData} from "./useAgendaPrintData";

const getPrintoutTypeLabel = (type?: Printout["type"]) => {
    switch (type) {
        case PrintoutType.AGENDA_HANDOUT:
            return "Agenda handout";
        default:
            return formatPrintStatus(type);
    }
};

// Agenda ids are profile ids; optional start/end query params override the stay window.
const AgendaPrintPage = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [selectedLayout, setSelectedLayout] = useState<PrintoutLayoutSelection | undefined>();
    const printData = useAgendaPrintData(
        id,
        selectedLayout?.printoutId,
        searchParams.get("start"),
        searchParams.get("end"),
        selectedLayout?.language,
    );

    const selectedPrintout = printData.selectedPrintout;
    const printLanguage = printData.language;
    const guestLanguage = printData.profile?.languageCode;
    const selectedPrintoutLanguage = selectedPrintout
        ? formatPrintNativeLanguage(printLanguage)
        : undefined;
    const documentVariables = usePrintoutDocumentVariables(selectedPrintout, printData.context, printLanguage);

    const resolvedValue = (key: string) => (
        printData.context
            ? resolvePrintoutVariable(key, printData.context)?.displayValue || EMPTY_PRINT_VALUE
            : EMPTY_PRINT_VALUE
    );

    const sectionCount = (sectorType: SectorType) => (
        printData.agendaItems.filter(item => item.sectorType === sectorType).length
    );

    const guestRows = [
        {label: t("Guest"), value: resolvedValue("guestFullName")},
        {label: t("Email"), value: resolvedValue("guestEmail")},
        {label: t("Phone"), value: resolvedValue("guestPhone")},
    ];

    const stayRows = [
        {label: t("From"), value: resolvedValue("agendaStartDate")},
        {label: t("To"), value: resolvedValue("agendaEndDate")},
        {label: t("Rooms"), value: resolvedValue("resRoomNames")},
    ];

    const agendaRows = [
        {label: t("Entries"), value: String(printData.agendaItems.length)},
        {label: t("Weekly Planner"), value: String(sectionCount(SectorType.WEEKLY_PLANNER))},
        {label: t("Spa"), value: String(sectionCount(SectorType.SPA))},
        {label: t("Restaurant"), value: String(sectionCount(SectorType.RESTAURANT))},
        {label: t("Other"), value: String(sectionCount(SectorType.OTHER))},
    ];

    const closePreview = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        window.close();
    };

    return (
        <PrintPreviewPageShell
            actions={(
                <PrintPreviewPrintButton
                    disabled={!selectedPrintout || !printData.context}
                    onClick={() => window.print()}
                />
            )}
            sidebar={(
                <PrintPreviewSidebarContent
                    alert={guestLanguage ? (
                        <>
                            {t("Guest language")}: <strong>{formatPrintNativeLanguage(guestLanguage)}</strong>
                        </>
                    ) : undefined}
                    onClose={closePreview}
                    selects={[
                        {
                            id: "agenda-printout-layout",
                            disabled: printData.isLoading || !printData.printouts.length,
                            emptyLabel: t("No agenda printouts"),
                            label: t("Printout"),
                            onChange: value => setSelectedLayout(parsePrintoutLayoutSelection(value)),
                            options: getPrintoutLayoutOptions(
                                printData.printouts,
                                formatPrintNativeLanguage,
                                t("Untitled printout"),
                            ),
                            value: getPrintoutLayoutSelectionValue(selectedPrintout, printLanguage),
                        },
                    ]}
                    tables={[
                        {rows: guestRows},
                        {rows: stayRows},
                        {rows: agendaRows},
                    ]}
                    printoutInfo={selectedPrintout ? {
                        language: selectedPrintoutLanguage,
                        type: t(getPrintoutTypeLabel(selectedPrintout.type)),
                    } : undefined}
                    title={t("Agenda Print")}
                    variables={documentVariables}
                />
            )}
        >
            {printData.isLoading && (
                <Stack alignItems="center" justifyContent="center" minHeight="100vh" spacing={2}>
                    <CircularProgress/>
                    <Typography color="text.secondary">
                        {t("Loading agenda print preview")}
                    </Typography>
                </Stack>
            )}

            {!printData.isLoading && printData.errorMessage && (
                <Box maxWidth={720} py={4} width="100%">
                    <Alert severity="error">
                        {printData.errorMessage}
                    </Alert>
                </Box>
            )}

            {!printData.isLoading && !printData.errorMessage && !selectedPrintout && (
                <Box maxWidth={720} py={4} width="100%">
                    <Alert severity="info">
                        {t("No agenda printout was found.")}
                    </Alert>
                </Box>
            )}

            {!printData.isLoading && selectedPrintout && printData.context && (
                <PrintoutDocumentRenderer
                    context={printData.context}
                    language={printLanguage}
                    printout={selectedPrintout}
                />
            )}
        </PrintPreviewPageShell>
    );
};

export default AgendaPrintPage;
